import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowLeft, Globe, List, X } from "@phosphor-icons/react";
import AuthContext from "../../context/AuthContext";
import { rememberLanguage } from "../../components/ui/LanguageToggle";
import { publicPaths } from "../../rendering/publicRoutes";
import { isEnglishPath, localizedPath, stripLocalePrefix } from "../../utils/seoLinks";
import LanguageSuggestion from "./LanguageSuggestion";
import "./siteNav.css";

// The header, at the top of the window over every section: on the page's
// stage at its top, floating in the page's column once the page moves under
// it (siteNav.css).

// Where the header leads: to the homepage's section holding the id
// `section`, or to another page, in the page's language.
const PLACES = [
  { key: "try", section: "prova-video" },
  { key: "plans", to: "/plans" },
  { key: "manifesto", to: "/manifesto" },
];

// What the header offers beside its places: a visitor is asked to try AVRAH
// and may sign in; someone signed in gets the app and their account, as the
// site's own header offers them (components/Header). The login dialog lives
// in that header, which opens it from ?action=login.
const ACTIONS = {
  visitor: { quiet: { key: "logIn", to: "/plans?action=login" }, main: { key: "tryFree", to: "/signup" } },
  member: { quiet: { key: "account", to: "/account-settings" }, main: { key: "download", to: "/download" } },
};

const WORDS = {
  it: {
    try: "Prova il tuo video", plans: "Piani", manifesto: "Manifesto",
    logIn: "Accedi", tryFree: "Prova gratis", account: "Impostazioni account", download: "Scarica l'app",
    skip: "Vai al contenuto", nav: "Principale", menu: "Menu", close: "Chiudi", home: "Torna alla home",
  },
  en: {
    try: "Try your video", plans: "Plans", manifesto: "Manifesto",
    logIn: "Log in", tryFree: "Try it free", account: "Account settings", download: "Download the app",
    skip: "Skip to content", nav: "Main", menu: "Menu", close: "Close", home: "Back to home",
  },
};

// `to` in `lang`, where the site has the page in both languages; sign-up
// and the account have one page for both.
const pagePath = (to, lang) => (publicPaths.includes(to.split("?")[0]) ? localizedPath(to, lang) : to);

// The other language, which the header offers the same page in.
const OTHER = {
  it: { lang: "en", code: "EN", name: "English" },
  en: { lang: "it", code: "IT", name: "Italiano" },
};

// Four times the 24 px it is shown at, so it stays sharp on any phone's
// screen and zoomed in; lossless, as the larger PNG it was cut from.
const LOGO = <img src="/avrah-logo-white.webp" alt="A.V.R.A.H." />;

// The logo leads home, except on the homepage itself, where a link to the
// page the visitor is already on would only reload it.
function HomeLogo({ lang, onHome, onClick }) {
  if (onHome) return <span className="sn-logo">{LOGO}</span>;
  return <Link to={localizedPath("/", lang)} className="sn-logo" onClick={onClick}>{LOGO}</Link>;
}

// Whether the page has left its top.
function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const read = () => setScrolled(window.scrollY > 0);
    read();
    window.addEventListener("scroll", read, { passive: true });
    return () => window.removeEventListener("scroll", read);
  }, []);
  return scrolled;
}

// A link to `place`: into the homepage, from it or from another of the
// site's pages, or to another page.
function PlaceLink({ place, lang, onHome, onClick }) {
  const label = WORDS[lang][place.key];
  if (place.to) return <NavLink to={pagePath(place.to, lang)} onClick={onClick}>{label}</NavLink>;
  return <a href={`${onHome ? "" : localizedPath("/", lang)}#${place.section}`} onClick={onClick}>{label}</a>;
}

// The page the visitor is on, in the other language.
function LanguageLink({ lang, page, className, onClick }) {
  const other = OTHER[lang];
  return (
    <Link
      className={className}
      to={localizedPath(page, other.lang)}
      hrefLang={other.lang}
      lang={other.lang}
      aria-label={other.name}
      onClick={() => {
        rememberLanguage(lang, other.lang);
        onClick?.();
      }}
    >
      <Globe aria-hidden="true" />
      <span aria-hidden="true">{other.code}</span>
    </Link>
  );
}

export default function SiteNav() {
  const { isLoggedIn } = useContext(AuthContext);
  const { quiet, main } = ACTIONS[isLoggedIn ? "member" : "visitor"];
  const { pathname } = useLocation();
  const page = stripLocalePrefix(pathname);
  const onHome = page === "/";
  const onManifesto = page === "/manifesto";
  const lang = isEnglishPath(pathname) ? "en" : "it";
  const words = WORDS[lang];
  const homeLabel = words.home;
  const scrolled = useScrolled();
  const sheet = useRef(null);
  const closer = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const lost = onHome && PLACES.find((place) => place.section && !document.getElementById(place.section));
    if (lost) throw new Error(`SiteNav: '${lost.key}' leads to #${lost.section}, which is not on the page`);
  }, [onHome]);
  useEffect(() => {
    const dialog = sheet.current;
    if (open && !dialog.open) {
      dialog.showModal();
      closer.current.focus();
    }
  }, [open]);
  // Closing the sheet first lets the page behind it scroll to a place.
  const close = () => sheet.current.close();

  return (
    <header className="sn" data-scrolled={scrolled}>
      <a className="sn-skip" href="#contenuto">{words.skip}</a>
      <nav aria-label={words.nav} className={`sn-bar hf-wrap${onManifesto ? " sn-with-home" : ""}`}>
        {onManifesto && (
          <Link to={localizedPath("/", lang)} className="btn btn-quiet sn-home" aria-label={homeLabel}>
            <ArrowLeft aria-hidden="true" />
            <span className="sn-home-label">{homeLabel}</span>
            <span className="sn-home-short" aria-hidden="true">Home</span>
          </Link>
        )}
        <HomeLogo lang={lang} onHome={onHome} />
        <ul className="sn-places">
          {PLACES.map((place) => (
            <li key={place.key}><PlaceLink place={place} lang={lang} onHome={onHome} /></li>
          ))}
        </ul>
        <LanguageLink className="sn-lang" lang={lang} page={page} />
        <Link className="sn-quiet" to={pagePath(quiet.to, lang)}>{words[quiet.key]}</Link>
        <Link className="btn btn-gold" to={pagePath(main.to, lang)}>{words[main.key]}</Link>
        <button type="button" className="sn-toggle" aria-expanded={open} aria-controls="sn-sheet" onClick={() => setOpen(true)}>
          <List aria-hidden="true" />
          <span>{words.menu}</span>
        </button>
      </nav>
      {/* For windows too narrow for the bar's places: they, the quiet
          action and the other language, over the whole window under a copy
          of the bar. The browser keeps focus in it, closes it on Escape and
          gives focus back to the menu button. */}
      <dialog ref={sheet} id="sn-sheet" className="sn-sheet" aria-label={words.menu} onClose={() => setOpen(false)}>
        <div className="sn-bar hf-wrap">
          <HomeLogo lang={lang} onHome={onHome} onClick={close} />
          <Link className="btn btn-gold" to={pagePath(main.to, lang)} onClick={close}>{words[main.key]}</Link>
          <button ref={closer} type="button" className="sn-toggle" onClick={close}>
            <X aria-hidden="true" />
            <span>{words.close}</span>
          </button>
        </div>
        <ul className="sn-sheet-places hf-wrap">
          {PLACES.map((place) => (
            <li key={place.key}><PlaceLink place={place} lang={lang} onHome={onHome} onClick={close} /></li>
          ))}
          <li className="sn-sheet-quiet">
            <Link className="btn btn-quiet" to={pagePath(quiet.to, lang)} onClick={close}>{words[quiet.key]}</Link>
          </li>
          <li>
            <LanguageLink className="sn-sheet-lang" lang={lang} page={page} onClick={close} />
          </li>
        </ul>
      </dialog>
      <LanguageSuggestion lang={lang} page={page} />
    </header>
  );
}
